import { useMutation } from '@tanstack/react-query';

import { UploadApurementDossiersFileViolationsDto } from 'lib_api/lib/api/gen';

import { useApi } from 'hooks/ApiStoreContext';
import { backAlertMessage } from 'hooks/utils/backAlertMessage';
import {
  ApurementDossierFormField,
  ApurementDossierFormValues,
} from 'pages/admin-fonctionnel/AdministrationAvancee/ApurementDossier/types';

interface ApurementDossierValidateFieldRet {
  validateApurementField: (
    field: 'FILE',
    values: ApurementDossierFormValues,
  ) => Promise<void>;
  isPending: boolean;
}

export function useValidateFieldApurementDossier(): ApurementDossierValidateFieldRet {
  const { AdminFonctionnelControllerApi: controller } = useApi();

  const validateApurementFieldMutation = useMutation<
    UploadApurementDossiersFileViolationsDto,
    Error,
    { field: ApurementDossierFormField } & ApurementDossierFormValues,
    void
  >({
    mutationFn: ({ field, file }) =>
      controller.validateUploadApurementDossiersFieldUsingPOST(field, file),
    onError: async (error: Error) => {
      const errorMessage =
        error instanceof Response
          ? (await backAlertMessage(error)).description
          : String(error);

      throw new Error(errorMessage);
    },
  });

  const validateApurementField = async (
    field: 'FILE',
    values: ApurementDossierFormValues,
  ) => {
    if (!values.file) {
      throw new Error('Le fichier est requis.');
    }

    const violations = await validateApurementFieldMutation.mutateAsync({
      field,
      ...values,
    });
    if (!violations?.uploadApurementDossiersViolationDtos) {
      throw new Error('Unexpected error during field validation.');
    }
    if (violations.uploadApurementDossiersViolationDtos.length === 0) {
      return;
    }
    throw new Error(violations.uploadApurementDossiersViolationDtos[0].message);
  };

  return {
    validateApurementField,
    isPending: validateApurementFieldMutation.isPending,
  };
}
